import * as THREE from 'three';

/**
 * ULTRA PERFORMANS: Adaptive Quality System
 * Frame rate'e göre otomatik kalite ayarlama
 *
 * Features:
 * - Real-time FPS monitoring
 * - Dynamic resolution scaling
 * - LOD level adjustment
 * - Shadow quality scaling
 * - Effect toggle based on performance
 * - GPU/CPU load balancing
 *
 * Benefits:
 * - Guaranteed stable frame rate
 * - Automatic quality optimization
 * - Smooth transitions
 * - Hardware-adaptive rendering
 */

interface QualityLevel {
  name: string;
  resolutionScale: number;          // 0.25-1.0
  lodBias: number;                  // Force lower LOD levels
  shadowMapSize: number;            // Shadow map resolution
  shadowEnabled: boolean;
  antialiasing: boolean;
  postProcessing: boolean;
  maxDrawCalls: number;
  particleMultiplier: number;       // 0-1
  reflectionQuality: number;        // 0-1
}

interface PerformanceMetrics {
  fps: number;
  frameTime: number;
  gpuTime: number;
  cpuTime: number;
  drawCalls: number;
  triangles: number;
  memoryUsage: number;
}

const QUALITY_PRESETS: Record<string, QualityLevel> = {
  ultra: {
    name: 'Ultra',
    resolutionScale: 1.0,
    lodBias: 0,
    shadowMapSize: 4096,
    shadowEnabled: true,
    antialiasing: true,
    postProcessing: true,
    maxDrawCalls: Infinity,
    particleMultiplier: 1.0,
    reflectionQuality: 1.0
  },
  high: {
    name: 'High',
    resolutionScale: 1.0,
    lodBias: 0,
    shadowMapSize: 2048,
    shadowEnabled: true,
    antialiasing: true,
    postProcessing: true,
    maxDrawCalls: 5000,
    particleMultiplier: 0.75,
    reflectionQuality: 0.75
  },
  medium: {
    name: 'Medium',
    resolutionScale: 0.75,
    lodBias: 1,
    shadowMapSize: 1024,
    shadowEnabled: true,
    antialiasing: true,
    postProcessing: false,
    maxDrawCalls: 2000,
    particleMultiplier: 0.5,
    reflectionQuality: 0.5
  },
  low: {
    name: 'Low',
    resolutionScale: 0.5,
    lodBias: 2,
    shadowMapSize: 512,
    shadowEnabled: false,
    antialiasing: false,
    postProcessing: false,
    maxDrawCalls: 1000,
    particleMultiplier: 0.25,
    reflectionQuality: 0.25
  },
  potato: {
    name: 'Potato',
    resolutionScale: 0.25,
    lodBias: 3,
    shadowMapSize: 256,
    shadowEnabled: false,
    antialiasing: false,
    postProcessing: false,
    maxDrawCalls: 500,
    particleMultiplier: 0.1,
    reflectionQuality: 0
  }
};

export class FikoAdaptiveQuality {
  private renderer: THREE.WebGLRenderer;
  private scene: THREE.Scene;
  private camera: THREE.Camera;

  // Current quality state
  private currentLevel: QualityLevel;
  private targetFPS = 60;
  private minFPS = 30;

  // Frame timing
  private frameTimeHistory: number[] = [];
  private historySize = 60;
  private lastFrameTime = 0;

  // GPU timing (EXT_disjoint_timer_query)
  private gpuTimerQuery: WebGLQuery | null = null;
  private gpuTimeHistory: number[] = [];

  // Resolution scaling
  private baseWidth = 0;
  private baseHeight = 0;
  private currentScale = 1.0;

  // Render target for resolution scaling
  private scaledRenderTarget: THREE.WebGLRenderTarget | null = null;
  private upscaleMaterial: THREE.ShaderMaterial | null = null;

  // Configuration
  private config = {
    enabled: true,
    autoAdjust: true,
    adjustInterval: 500, // ms between quality adjustments
    smoothTransitions: true,
    transitionSpeed: 0.1,
    fpsThresholdUp: 0.9, // Go up if FPS > target * 0.9
    fpsThresholdDown: 0.7, // Go down if FPS < target * 0.7
    hysteresis: 2000 // Don't change quality for 2s after change
  };

  // State
  private lastAdjustTime = 0;
  private pendingScaleTarget = 1.0;
  private qualityLocked = false;

  // Stats
  private stats = {
    currentQuality: 'high',
    currentFPS: 60,
    averageFrameTime: 16.67,
    adjustmentsUp: 0,
    adjustmentsDown: 0,
    resolutionScale: 1.0
  };

  constructor(
    renderer: THREE.WebGLRenderer,
    scene: THREE.Scene,
    camera: THREE.Camera
  ) {
    this.renderer = renderer;
    this.scene = scene;
    this.camera = camera;

    // Start at high quality
    this.currentLevel = { ...QUALITY_PRESETS.high };
    this.stats.currentQuality = 'high';

    // Get base resolution
    const size = renderer.getSize(new THREE.Vector2());
    this.baseWidth = size.x;
    this.baseHeight = size.y;

    // Initialize GPU timer if available
    this.initGPUTimer();

    // Create resolution scaling resources
    this.initResolutionScaling();
  }

  /**
   * Initialize GPU timer query
   */
  private initGPUTimer(): void {
    const gl = this.renderer.getContext();
    const ext = gl.getExtension('EXT_disjoint_timer_query_webgl2');

    if (ext && gl instanceof WebGL2RenderingContext) {
      this.gpuTimerQuery = gl.createQuery();
    }
  }

  /**
   * Initialize resolution scaling render target
   */
  private initResolutionScaling(): void {
    this.scaledRenderTarget = new THREE.WebGLRenderTarget(
      this.baseWidth,
      this.baseHeight,
      {
        format: THREE.RGBAFormat,
        type: THREE.UnsignedByteType,
        minFilter: THREE.LinearFilter,
        magFilter: THREE.LinearFilter
      }
    );

    // Upscale shader (bilinear with sharpening)
    this.upscaleMaterial = new THREE.ShaderMaterial({
      uniforms: {
        tDiffuse: { value: null },
        uResolution: { value: new THREE.Vector2() },
        uSharpness: { value: 0.5 }
      },
      vertexShader: `
        varying vec2 vUv;
        void main() {
          vUv = uv;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        uniform sampler2D tDiffuse;
        uniform vec2 uResolution;
        uniform float uSharpness;
        varying vec2 vUv;

        void main() {
          vec4 color = texture2D(tDiffuse, vUv);

          // Simple sharpening
          vec2 texel = 1.0 / uResolution;
          vec4 blur = texture2D(tDiffuse, vUv + texel * vec2(-1, 0))
                    + texture2D(tDiffuse, vUv + texel * vec2(1, 0))
                    + texture2D(tDiffuse, vUv + texel * vec2(0, -1))
                    + texture2D(tDiffuse, vUv + texel * vec2(0, 1));
          blur *= 0.25;

          color.rgb = mix(color.rgb, color.rgb + (color.rgb - blur.rgb) * uSharpness, uSharpness);

          gl_FragColor = color;
        }
      `
    });
  }

  /**
   * Update quality system - call every frame
   */
  update(): void {
    if (!this.config.enabled) return;

    const now = performance.now();

    // Track frame time
    if (this.lastFrameTime > 0) {
      const frameTime = now - this.lastFrameTime;
      this.frameTimeHistory.push(frameTime);

      if (this.frameTimeHistory.length > this.historySize) {
        this.frameTimeHistory.shift();
      }
    }
    this.lastFrameTime = now;

    // Update stats
    this.updateStats();

    // Smooth resolution transitions
    if (this.config.smoothTransitions && this.currentScale !== this.pendingScaleTarget) {
      this.currentScale += (this.pendingScaleTarget - this.currentScale) * this.config.transitionSpeed;

      if (Math.abs(this.currentScale - this.pendingScaleTarget) < 0.01) {
        this.currentScale = this.pendingScaleTarget;
      }

      this.applyResolutionScale(this.currentScale);
    }

    // Auto-adjust quality
    if (this.config.autoAdjust && !this.qualityLocked) {
      if (now - this.lastAdjustTime > this.config.adjustInterval) {
        this.autoAdjustQuality();
        this.lastAdjustTime = now;
      }
    }
  }

  /**
   * Update performance statistics
   */
  private updateStats(): void {
    if (this.frameTimeHistory.length === 0) return;

    const avgFrameTime = this.frameTimeHistory.reduce((a, b) => a + b) /
      this.frameTimeHistory.length;

    this.stats.averageFrameTime = avgFrameTime;
    this.stats.currentFPS = 1000 / avgFrameTime;
    this.stats.resolutionScale = this.currentScale;
  }

  /**
   * Auto-adjust quality based on FPS
   */
  private autoAdjustQuality(): void {
    const currentFPS = this.stats.currentFPS;
    const targetFPS = this.targetFPS;

    // Check if we need to go down
    if (currentFPS < targetFPS * this.config.fpsThresholdDown) {
      this.decreaseQuality();
      return;
    }

    // Check if we can go up
    if (currentFPS > targetFPS * this.config.fpsThresholdUp) {
      this.increaseQuality();
      return;
    }
  }

  /**
   * Decrease quality level
   */
  private decreaseQuality(): void {
    const levels = ['ultra', 'high', 'medium', 'low', 'potato'];
    const currentIndex = levels.indexOf(this.stats.currentQuality);

    if (currentIndex < levels.length - 1) {
      const newLevel = levels[currentIndex + 1];
      this.setQuality(newLevel);
      this.stats.adjustmentsDown++;

      // Apply hysteresis
      this.qualityLocked = true;
      setTimeout(() => {
        this.qualityLocked = false;
      }, this.config.hysteresis);
    }
  }

  /**
   * Increase quality level
   */
  private increaseQuality(): void {
    const levels = ['ultra', 'high', 'medium', 'low', 'potato'];
    const currentIndex = levels.indexOf(this.stats.currentQuality);

    if (currentIndex > 0) {
      const newLevel = levels[currentIndex - 1];
      this.setQuality(newLevel);
      this.stats.adjustmentsUp++;

      // Apply hysteresis
      this.qualityLocked = true;
      setTimeout(() => {
        this.qualityLocked = false;
      }, this.config.hysteresis);
    }
  }

  /**
   * Set quality level by name
   */
  setQuality(levelName: string): void {
    const preset = QUALITY_PRESETS[levelName];
    if (!preset) return;

    this.currentLevel = { ...preset };
    this.stats.currentQuality = levelName;

    // Apply resolution scale
    this.pendingScaleTarget = preset.resolutionScale;
    if (!this.config.smoothTransitions) {
      this.currentScale = preset.resolutionScale;
      this.applyResolutionScale(this.currentScale);
    }

    // Apply shadow settings
    this.applyShadowSettings();

    // Apply antialiasing
    this.applyAntialiasing();

  }

  /**
   * Apply resolution scale
   */
  private applyResolutionScale(scale: number): void {
    if (!this.scaledRenderTarget) return;

    const newWidth = Math.floor(this.baseWidth * scale);
    const newHeight = Math.floor(this.baseHeight * scale);

    this.scaledRenderTarget.setSize(newWidth, newHeight);

    if (this.upscaleMaterial) {
      this.upscaleMaterial.uniforms.uResolution.value.set(newWidth, newHeight);
    }
  }

  /**
   * Apply shadow settings
   */
  private applyShadowSettings(): void {
    this.renderer.shadowMap.enabled = this.currentLevel.shadowEnabled;

    // Update shadow map sizes for all lights
    this.scene.traverse(obj => {
      if (obj instanceof THREE.DirectionalLight ||
          obj instanceof THREE.SpotLight ||
          obj instanceof THREE.PointLight) {
        if (obj.shadow && obj.shadow.map) {
          // Would need to recreate shadow map at new size
          // This is expensive, so only do on major quality changes
        }
      }
    });
  }

  /**
   * Apply antialiasing settings
   */
  private applyAntialiasing(): void {
    // WebGL renderer antialiasing can't be changed after creation
    // This would need to be handled at renderer creation time
  }

  /**
   * Render with resolution scaling
   */
  renderScaled(renderFunction: () => void): void {
    if (this.currentScale >= 0.99 || !this.scaledRenderTarget) {
      // Full resolution, render directly
      renderFunction();
      return;
    }

    // Render to scaled target
    const currentTarget = this.renderer.getRenderTarget();
    this.renderer.setRenderTarget(this.scaledRenderTarget);
    renderFunction();

    // Upscale to screen
    this.renderer.setRenderTarget(currentTarget);

    // Use upscale shader
    if (this.upscaleMaterial) {
      this.upscaleMaterial.uniforms.tDiffuse.value = this.scaledRenderTarget.texture;

      const quad = new THREE.Mesh(
        new THREE.PlaneGeometry(2, 2),
        this.upscaleMaterial
      );
      const orthoCamera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);

      this.renderer.render(new THREE.Scene().add(quad), orthoCamera);
    }
  }

  /**
   * Get current quality level
   */
  getCurrentLevel(): QualityLevel {
    return { ...this.currentLevel };
  }

  /**
   * Get performance metrics
   */
  getMetrics(): PerformanceMetrics {
    const info = this.renderer.info;

    return {
      fps: this.stats.currentFPS,
      frameTime: this.stats.averageFrameTime,
      gpuTime: this.gpuTimeHistory.length > 0
        ? this.gpuTimeHistory.reduce((a, b) => a + b) / this.gpuTimeHistory.length
        : 0,
      cpuTime: this.stats.averageFrameTime,
      drawCalls: info.render.calls,
      triangles: info.render.triangles,
      memoryUsage: info.memory.geometries + info.memory.textures
    };
  }

  /**
   * Get statistics
   */
  getStats(): {
    currentQuality: string;
    currentFPS: number;
    targetFPS: number;
    averageFrameTime: string;
    resolutionScale: string;
    effectiveResolution: string;
    adjustmentsUp: number;
    adjustmentsDown: number;
  } {
    const effectiveWidth = Math.floor(this.baseWidth * this.currentScale);
    const effectiveHeight = Math.floor(this.baseHeight * this.currentScale);

    return {
      currentQuality: this.stats.currentQuality,
      currentFPS: Math.round(this.stats.currentFPS),
      targetFPS: this.targetFPS,
      averageFrameTime: `${this.stats.averageFrameTime.toFixed(2)}ms`,
      resolutionScale: `${(this.currentScale * 100).toFixed(0)}%`,
      effectiveResolution: `${effectiveWidth}x${effectiveHeight}`,
      adjustmentsUp: this.stats.adjustmentsUp,
      adjustmentsDown: this.stats.adjustmentsDown
    };
  }

  /**
   * Set target FPS
   */
  setTargetFPS(fps: number): void {
    this.targetFPS = Math.max(30, Math.min(144, fps));
  }

  /**
   * Configure system
   */
  configure(config: Partial<typeof this.config>): void {
    Object.assign(this.config, config);
  }

  /**
   * Force quality level (disable auto-adjust temporarily)
   */
  forceQuality(levelName: string, duration: number = 5000): void {
    this.setQuality(levelName);
    this.qualityLocked = true;

    setTimeout(() => {
      this.qualityLocked = false;
    }, duration);
  }

  /**
   * Reset to default quality
   */
  reset(): void {
    this.setQuality('high');
    this.frameTimeHistory = [];
    this.gpuTimeHistory = [];
    this.stats.adjustmentsUp = 0;
    this.stats.adjustmentsDown = 0;
    this.qualityLocked = false;
  }

  /**
   * Handle resize
   */
  resize(width: number, height: number): void {
    this.baseWidth = width;
    this.baseHeight = height;
    this.applyResolutionScale(this.currentScale);
  }

  /**
   * Cleanup
   */
  dispose(): void {
    if (this.scaledRenderTarget) {
      this.scaledRenderTarget.dispose();
    }

    if (this.upscaleMaterial) {
      this.upscaleMaterial.dispose();
    }

    if (this.gpuTimerQuery) {
      const gl = this.renderer.getContext();
      if (gl instanceof WebGL2RenderingContext) {
        gl.deleteQuery(this.gpuTimerQuery);
      }
    }
  }
}

/**
 * ULTRA PERFORMANS: Performance Profiler
 * Detailed performance analysis
 */
export class FikoPerformanceProfiler {
  private renderer: THREE.WebGLRenderer;

  private samples: Array<{
    timestamp: number;
    frameTime: number;
    drawCalls: number;
    triangles: number;
    textures: number;
    geometries: number;
  }> = [];

  private maxSamples = 300; // 5 seconds at 60fps
  private isRecording = false;

  constructor(renderer: THREE.WebGLRenderer) {
    this.renderer = renderer;
  }

  /**
   * Start recording
   */
  startRecording(): void {
    this.samples = [];
    this.isRecording = true;
  }

  /**
   * Stop recording
   */
  stopRecording(): void {
    this.isRecording = false;
  }

  /**
   * Record frame
   */
  recordFrame(frameTime: number): void {
    if (!this.isRecording) return;

    const info = this.renderer.info;

    this.samples.push({
      timestamp: performance.now(),
      frameTime,
      drawCalls: info.render.calls,
      triangles: info.render.triangles,
      textures: info.memory.textures,
      geometries: info.memory.geometries
    });

    if (this.samples.length > this.maxSamples) {
      this.samples.shift();
    }
  }

  /**
   * Get profile report
   */
  getReport(): {
    averageFPS: number;
    minFPS: number;
    maxFPS: number;
    frameTimeVariance: number;
    averageDrawCalls: number;
    averageTriangles: number;
    spikes: number;
  } {
    if (this.samples.length === 0) {
      return {
        averageFPS: 0,
        minFPS: 0,
        maxFPS: 0,
        frameTimeVariance: 0,
        averageDrawCalls: 0,
        averageTriangles: 0,
        spikes: 0
      };
    }

    const frameTimes = this.samples.map(s => s.frameTime);
    const avgFrameTime = frameTimes.reduce((a, b) => a + b) / frameTimes.length;
    const minFrameTime = Math.min(...frameTimes);
    const maxFrameTime = Math.max(...frameTimes);

    const variance = frameTimes
      .map(t => Math.pow(t - avgFrameTime, 2))
      .reduce((a, b) => a + b) / frameTimes.length;

    const spikes = frameTimes.filter(t => t > avgFrameTime * 2).length;

    return {
      averageFPS: 1000 / avgFrameTime,
      minFPS: 1000 / maxFrameTime,
      maxFPS: 1000 / minFrameTime,
      frameTimeVariance: Math.sqrt(variance),
      averageDrawCalls: this.samples.reduce((a, s) => a + s.drawCalls, 0) / this.samples.length,
      averageTriangles: this.samples.reduce((a, s) => a + s.triangles, 0) / this.samples.length,
      spikes
    };
  }

  /**
   * Clear samples
   */
  clear(): void {
    this.samples = [];
  }
}
